import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ScrollArea } from "../ui/scroll-area";
import { formatDateTimeIST } from "../../utils/dateUtils";
import { getStatusIcon } from "./nocUtils";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Search,
  Filter,
  Brain,
  Target,
  Eye,
  Image as ImageIcon
} from "lucide-react";

export const StepTestingTab = ({
  stepStats,
  stepLogs,
  stepFilters,
  setStepFilters,
  stepPagination,
  setStepPagination,
  fetchStepLogs,
  onViewDetail
}) => {
  return (
    <div className="space-y-4" data-testid="step-testing-tab">
      {/* Step Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {stepStats.slice(0, 6).map((stat) => (
          <Card key={stat.step_name} className="relative overflow-hidden">
            <div
              className="absolute top-0 left-0 w-1 h-full"
              style={{ backgroundColor: stat.pass_rate >= 80 ? '#10b981' : (stat.pass_rate >= 50 ? '#f59e0b' : '#ef4444') }}
            />
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium truncate">{stat.step_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{stat.pass_rate}%</p>
                  <p className="text-xs text-muted-foreground">Pass Rate</p>
                </div>
                <div className="text-right text-sm">
                  <div className="flex items-center gap-1 text-emerald-600">
                    <CheckCircle2 className="w-3 h-3" /> {stat.passed}
                  </div>
                  <div className="flex items-center gap-1 text-red-600">
                    <XCircle className="w-3 h-3" /> {stat.failed}
                  </div>
                  {stat.warnings > 0 && (
                    <div className="flex items-center gap-1 text-amber-600">
                      <AlertTriangle className="w-3 h-3" /> {stat.warnings}
                    </div>
                  )}
                </div>
              </div>
              {stat.avg_confidence > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Avg AI Confidence: {stat.avg_confidence}%
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Step Logs Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search step name..."
                className="pl-9 w-[200px]"
                value={stepFilters.step_name}
                onChange={(e) => setStepFilters({...stepFilters, step_name: e.target.value})}
              />
            </div>
            <Select
              value={stepFilters.status}
              onValueChange={(v) => setStepFilters({...stepFilters, status: v === "all" ? "" : v})}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pass">Passed</SelectItem>
                <SelectItem value="fail">Failed</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchStepLogs}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="w-5 h-5" />
            Step Verification Logs
          </CardTitle>
          <CardDescription>Detailed logging of each verification step execution</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {stepLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No step logs found</p>
              ) : (
                stepLogs.map((log, idx) => (
                  <div
                    key={`${log.test_id}-${log.step_number}-${idx}`}
                    className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => onViewDetail(log.test_id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(log.step_status)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">Step {log.step_number}: {log.step_name}</span>
                            <Badge variant={log.step_status === "pass" ? "default" : (log.step_status === "fail" ? "destructive" : "secondary")}>
                              {log.step_status}
                            </Badge>
                            {log.has_image && (
                              <Badge variant="outline" className="text-xs">
                                <ImageIcon className="w-3 h-3 mr-1" />
                                Image
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Test: {log.serial_number || "No S/N"} • {log.product_name} • by {log.tester_name}
                          </p>
                          {log.ai_analysis && (
                            <div className="mt-2 flex items-center gap-2 text-xs">
                              <Brain className="w-3 h-3 text-blue-500" />
                              <span className="text-muted-foreground">
                                AI: {log.ai_analysis.result || "Analyzed"}
                                {log.ai_analysis.confidence && ` (${log.ai_analysis.confidence}% confidence)`}
                              </span>
                            </div>
                          )}
                          {log.notes && (
                            <p className="text-xs text-muted-foreground mt-1 italic">Note: {log.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-muted-foreground">{formatDateTimeIST(log.verified_at || log.test_started_at)}</p>
                        <Button variant="ghost" size="sm" className="mt-1">
                          <Eye className="w-3 h-3 mr-1" />
                          Detail
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {stepLogs.length} of {stepPagination.total} step logs
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={stepPagination.skip === 0}
                onClick={() => setStepPagination(p => ({...p, skip: Math.max(0, p.skip - p.limit)}))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={stepPagination.skip + stepPagination.limit >= stepPagination.total}
                onClick={() => setStepPagination(p => ({...p, skip: p.skip + p.limit}))}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
